import { IconButton } from '@apia/components';
import React from 'react';
import { InputProps, Input as TUIInput, ThemeUICSSObject } from '@apia/theme';
import { FormContext } from '../form/FormContext';
import { useFieldStatesClassNames } from '../hooks';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { validationsStore } from '../validationStore';
import { TDefaultFieldType } from './types';
import { FieldWrapper } from './FieldWrapper';

type TDeletableInput = InputProps & TDefaultFieldType;

export const DeletableInput = React.forwardRef<
  HTMLInputElement,
  TDeletableInput
>(
  (
    {
      avoidSemicolon,
      className,
      label,
      onChange: outerOnChange,
      type,
      /**
       * useField
       */
      avoidRegisterIfExists,
      formName: propsFormName,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { name: contextFormName } = React.useContext(FormContext);
    const formName = propsFormName ?? contextFormName;

    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });

    const {
      error,
      updateValidations,
      onChange,
      isTouched,
      ...validationProps
    } = useField<string>({
      formName,
      name,
      initialValue:
        initialValue ??
        (value as string) ??
        validationsStore.getFieldStore(formName, name)?.value ??
        '',
      removeOnUnmount,
      submitValueParser,
      validationFunction: actualValidationFunction,
      validationRules: actualValidationRules,
    });

    const handleOnChange = React.useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        onChange(ev);
        outerOnChange?.(ev);
      },
      [onChange, outerOnChange],
    );

    const stateClassName = useFieldStatesClassNames(name);

    return (
      <FieldWrapper
        name={name}
        label={label}
        sx={React.useCallback(
          () =>
            ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
            } as ThemeUICSSObject),
          [],
        )}
        className={`deletableInput ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
      >
        <TUIInput
          type={type}
          {...props}
          {...validationProps}
          aria-label={props['aria-label'] ?? label}
          ref={ref}
          onChange={handleOnChange}
          value={validationProps.value ?? ''}
          className={`deletableInput__input ${stateClassName ?? ''}`}
        />
        <IconButton
          icon="Close"
          className="deletableInput__deleteButton"
          onClick={() => onChange('')}
        />
      </FieldWrapper>
    );
  },
);

DeletableInput.displayName = 'DeletableInput';
